import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BrandLogo from './BrandLogo';
import alegionLogo from '../assets/images/alegion-logo.png';
import { colors, glass } from '../theme/designTokens';
import { navLinks } from '../data/navigation';

const NAVBAR_HEIGHT = { xs: 72, md: 88 };

const NavBar = ({ logo = alegionLogo, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  const lightText = dark && !scrolled;
  const linkColor = lightText ? 'rgba(226, 232, 240, 0.88)' : colors.navy;

  const navLinkSx = {
    color: linkColor,
    textDecoration: 'none',
    fontSize: '0.78rem',
    fontWeight: 500,
    letterSpacing: '0.04em',
    px: 1,
    py: 0.75,
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s ease',
    '&:hover': {
      color: lightText ? colors.white : colors.navy,
      background: lightText ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.05)',
    },
    '&.active': {
      color: lightText ? colors.white : colors.navy,
      fontWeight: 600,
      borderBottom: `1px solid ${lightText ? 'rgba(238,186,43,0.85)' : colors.navy}`,
      borderRadius: 0,
      background: 'transparent',
    },
  };

  return (
    <>
      <Box
        component={motion.nav}
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, md: 3, lg: 4 },
          py: scrolled ? 1 : 1.25,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 1100,
          minHeight: NAVBAR_HEIGHT,
          transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          ...(scrolled
            ? {
                ...glass.light,
                boxShadow: '0 4px 30px rgba(15, 23, 42, 0.08)',
              }
            : dark
              ? {
                  background: 'transparent',
                  borderBottom: '1px solid transparent',
                }
              : {
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderBottom: `1px solid ${colors.borderLight}`,
                }),
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/')}
          aria-label="ALEgION® home"
          sx={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <BrandLogo
            src={logo}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            imageSx={{
              height: { xs: 48, sm: 56, md: 56 },
              width: 'auto',
              maxWidth: { xs: 140, sm: 160, md: 180 },
              transition: 'height 0.35s ease',
            }}
          />
        </Box>

        {!isMobile && (
          <Box
            component="ul"
            sx={{
              display: 'flex',
              listStyle: 'none',
              m: 0,
              p: 0,
              alignItems: 'center',
              gap: 0.15,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {navLinks.map(({ label, path }) => (
              <Box component="li" key={path} sx={{ display: 'flex' }}>
                <NavLink to={path} end={path === '/'} style={{ textDecoration: 'none' }}>
                  {({ isActive }) => (
                    <Box
                      component={motion.div}
                      whileHover={{ y: -1 }}
                      sx={{
                        ...navLinkSx,
                        ...(isActive && {
                          fontWeight: 600,
                          borderBottom: `1px solid ${lightText ? 'rgba(238,186,43,0.85)' : colors.navy}`,
                          borderRadius: 0,
                          background: 'transparent',
                        }),
                      }}
                    >
                      {label}
                    </Box>
                  )}
                </NavLink>
              </Box>
            ))}
          </Box>
        )}

        {isMobile && (
          <IconButton
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            sx={{ color: lightText ? 'rgba(226, 232, 240, 0.9)' : colors.navy }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ height: dark ? 0 : NAVBAR_HEIGHT, flexShrink: 0 }} aria-hidden="true" />

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          display: { lg: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: '85vw', sm: 320 },
            maxWidth: 360,
            background: colors.navyDark,
            color: colors.white,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <BrandLogo
            src={logo}
            alt="ALEgION"
            imageSx={{ height: 48, width: 'auto', maxWidth: 140 }}
          />
          <IconButton onClick={() => setOpen(false)} sx={{ color: colors.white }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ px: 1, pt: 2 }}>
          {navLinks.map(({ label, path }) => (
            <ListItemButton
              key={path}
              onClick={() => handleNavigate(path)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontWeight: 500, fontSize: '1rem', color: colors.white }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default NavBar;
